import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Stack,
  LinearProgress,
  Chip,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Box,
} from '@mui/material';
import { EmojiEvents, Inventory2 } from '@mui/icons-material';
import api from '../../services/api';
import TextField from '../common/TextField';

type RankingItem = { productId: number; productName: string; sku?: string; units: number; revenue: number; orders: number };
type RankingResponse = { period: string; start: string; end: string; totalUnits: number; totalRevenue: number; items: RankingItem[] };

const PERIODS: Array<{ value: string; label: string }> = [
  { value: 'week', label: 'Semana' },
  { value: 'month', label: 'Mes' },
  { value: 'quarter', label: 'Trimestre' },
  { value: 'year', label: 'Año' },
];

const formatNumber = (val: number) => (val ?? 0).toLocaleString('es-AR', { maximumFractionDigits: 2 });
const formatCurrency = (val: number) => `$${formatNumber(val)}`;

const medalColor = (rank: number): string | undefined => {
  if (rank === 0) return '#FFD700';
  if (rank === 1) return '#C0C0C0';
  if (rank === 2) return '#CD7F32';
  return undefined;
};

const TopProductsReport: React.FC = () => {
  const [period, setPeriod] = useState('month');
  const [limit, setLimit] = useState(10);
  const [data, setData] = useState<RankingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.getTopProductsRanking({ period, limit });
      setData(result);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error al cargar el reporte');
    } finally {
      setLoading(false);
    }
  }, [period, limit]);

  // Al cambiar el período o el límite, se recalcula y re-rankea el listado.
  useEffect(() => {
    loadData();
  }, [loadData]);

  const items = data?.items || [];

  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <EmojiEvents color="primary" />
          <Typography variant="h6" fontWeight={600}>Top Productos Vendidos</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Ranking de los productos más vendidos por unidades. El listado se recalcula y reordena según el período seleccionado.
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={period}
            onChange={(_, v) => { if (v) setPeriod(v); }}
            color="primary"
          >
            {PERIODS.map(p => (
              <ToggleButton key={p.value} value={p.value}>{p.label}</ToggleButton>
            ))}
          </ToggleButtonGroup>

          <TextField
            size="small"
            type="number"
            label="Top N"
            value={limit}
            onChange={(e) => { const n = parseInt(e.target.value, 10); setLimit(Number.isFinite(n) && n > 0 ? n : 10); }}
            sx={{ width: 110 }}
            inputProps={{ min: 1, max: 100 }}
          />

          {data && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip icon={<Inventory2 />} label={`${formatNumber(data.totalUnits)} u`} variant="outlined" />
              <Chip label={formatCurrency(data.totalRevenue)} color="success" variant="outlined" />
            </Stack>
          )}
        </Stack>

        {loading && <LinearProgress sx={{ mb: 2 }} />}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!loading && !error && items.length === 0 && (
          <Alert severity="info">No hay ventas registradas en este período.</Alert>
        )}

        {!loading && items.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 48 }}>#</TableCell>
                  <TableCell>Producto</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell align="right">Unidades</TableCell>
                  <TableCell align="right">Facturación</TableCell>
                  <TableCell align="right">Órdenes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((it, idx) => (
                  <TableRow key={it.productId} hover>
                    <TableCell>
                      <Box
                        sx={{
                          width: 28, height: 28, borderRadius: '50%',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontWeight: 700, fontSize: 13,
                          bgcolor: medalColor(idx) || 'grey.200',
                          color: idx < 3 ? 'rgba(0,0,0,0.8)' : 'text.secondary',
                        }}
                      >
                        {idx + 1}
                      </Box>
                    </TableCell>
                    <TableCell><Typography variant="body2" fontWeight={idx < 3 ? 600 : 400}>{it.productName}</Typography></TableCell>
                    <TableCell><Typography variant="caption" color="text.secondary">{it.sku || '—'}</Typography></TableCell>
                    <TableCell align="right"><strong>{formatNumber(it.units)}</strong></TableCell>
                    <TableCell align="right">{formatCurrency(it.revenue)}</TableCell>
                    <TableCell align="right">{it.orders}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default TopProductsReport;
